import '../styles/reset.css';
import '../styles/tokens.css';
import '../styles/base.css';
import '../styles/utilities.css';
import '../styles/components/layout.css';
import '../styles/components/avatar.css';
import '../styles/components/select.css';
import '../styles/components/mood.css';
import '../styles/pages/projects.css';
import '../styles/pages/characters.css';
import '../styles/pages/beats.css';
import '../styles/pages/feed.css';
import '../styles/pages/reports.css';
import '../styles/pages/onboarding.css';

import { el } from './ui/lib/dom.js';
import { registerSW } from './ui/lib/pwa.js';

// Register service worker
registerSW();

// ?reset=true — wipe all data and start fresh
if (new URLSearchParams(window.location.search).has('reset')) {
  (async () => {
    const dbs = await indexedDB.databases?.() || [];
    await Promise.all(dbs.map(db => new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(db.name);
      req.onsuccess = resolve;
      req.onerror = resolve;
    })));
    localStorage.clear();
    sessionStorage.clear();
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    window.location.replace('/');
  })();
} else if (window.location.pathname === '/auth/callback') {
  import('./ui/pages/auth/CallbackPage.js').then(({ CallbackPage }) => {
    const app = document.getElementById('app');
    const { element } = CallbackPage();
    app.appendChild(element);
  });
} else {
  boot();
}

async function boot() {
  const { AppLayout } = await import('./ui/layouts/AppLayout.js');
  const { addRoute, setOutlet, setNotFound, startRouter, navigate } = await import('./ui/router.js');
  const { appSettings } = await import('./domain/stateManager.js');

  // Pages
  const { ProjectsPage } = await import('./ui/pages/story/ProjectsPage.js');
  const { StoryIndexPage } = await import('./ui/pages/story/StoryIndexPage.js');
  const { CharactersPage } = await import('./ui/pages/story/CharactersPage.js');
  const { CharacterEditorPage } = await import('./ui/pages/story/CharacterEditorPage.js');
  const { BeatsPage } = await import('./ui/pages/story/BeatsPage.js');
  const { BeatEditorPage } = await import('./ui/pages/story/BeatEditorPage.js');
  const { MoodPresetsPage } = await import('./ui/pages/story/MoodPresetsPage.js');
  const { CloudProjectsPage } = await import('./ui/pages/story/CloudProjectsPage.js');
  const { FeedPage } = await import('./ui/pages/social/FeedPage.js');
  const { NewPostPage } = await import('./ui/pages/social/NewPostPage.js');
  const { PostDetailPage } = await import('./ui/pages/social/PostDetailPage.js');
  const { ReportsIndexPage } = await import('./ui/pages/reports/ReportsIndexPage.js');
  const { CharacterArcPage } = await import('./ui/pages/reports/CharacterArcPage.js');
  const { MoodTrendsPage } = await import('./ui/pages/reports/MoodTrendsPage.js');
  const { RelationshipMapPage } = await import('./ui/pages/reports/RelationshipMapPage.js');
  const { BeatSummaryPage } = await import('./ui/pages/reports/BeatSummaryPage.js');
  const { OnboardingPage } = await import('./ui/pages/onboarding/OnboardingPage.js');
  const { CharacterProfilePage } = await import('./ui/pages/CharacterProfilePage.js');

  await appSettings.ready();

  const { layout, outlet } = AppLayout();

  const app = document.getElementById('app');
  app.appendChild(layout);

  setOutlet(outlet);

  // Onboarding
  addRoute('/onboarding', () => OnboardingPage());

  // Story routes
  addRoute('/story/projects', () => ProjectsPage());
  addRoute('/story/characters', () => CharactersPage());
  addRoute('/story/character-editor', () => CharacterEditorPage());
  addRoute('/story/character-editor/:id', ({ id }) => CharacterEditorPage({ id }));
  addRoute('/story/beats', () => BeatsPage());
  addRoute('/story/beat-editor', () => BeatEditorPage());
  addRoute('/story/beat-editor/:id', ({ id }) => BeatEditorPage({ id }));
  addRoute('/story/mood-presets', () => MoodPresetsPage());
  addRoute('/story/cloud', () => CloudProjectsPage());
  addRoute('/story', () => StoryIndexPage());

  // Social routes
  addRoute('/social/feed', () => FeedPage());
  addRoute('/social/new-post', () => NewPostPage());
  addRoute('/social/post/:id', ({ id }) => PostDetailPage({ id }));

  // Character profile
  addRoute('/character/:id', ({ id }) => CharacterProfilePage({ id }));

  // Reports
  addRoute('/reports/character-arc', () => CharacterArcPage());
  addRoute('/reports/mood-trends', () => MoodTrendsPage());
  addRoute('/reports/relationship-map', () => RelationshipMapPage());
  addRoute('/reports/beat-summary', () => BeatSummaryPage());
  addRoute('/reports', () => ReportsIndexPage());

  // Default route — send to onboarding if no projects, otherwise to story index
  addRoute('/', () => {
    const projects = appSettings.get('projects') || [];
    if (projects.length === 0) {
      navigate('/onboarding');
    } else {
      navigate('/story');
    }
    return { element: el('div') };
  });

  setNotFound(() => ({
    element: el('div', { className: 'empty-state' },
      el('div', { className: 'empty-state__icon' }, '?'),
      el('div', { className: 'empty-state__title' }, 'Page not found'),
    ),
  }));

  startRouter();
}
